// @flow
import React, { Fragment } from "react";
import idx from "idx";
import styled, { css } from "styled-components";
import RefreshIcon from "@material-ui/icons/RefreshOutlined";
import Gettext from "../common/Gettext";
import Money from "../Money";
import { TournamentAccount } from "../Tournaments";
import { tournamentCurrency } from "../Icons/Kuchka";
import { ActiveTournament } from "../Tournaments/Api";

import type { Node } from "react";
import type { Account as AccountType, Profile } from "../../redux/CommonTypes";
import { fontSize } from "../common/styled-mixins";

const InfinityIcon = React.memo(() => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 17.502 17.502"
    width="100%"
  >
    <path
      d="M13.182 4.431a4.282 4.282 0 0 0-3.036 1.246L8.75 7.017l-1.375-1.32C6.56 4.88 5.474 4.431 4.32 4.431s-2.239.451-3.053 1.266A4.288 4.288 0 0 0 0 8.752c0 1.152.449 2.238 1.266 3.053a4.285 4.285 0 0 0 3.054 1.266 4.272 4.272 0 0 0 3.036-1.248l1.395-1.338 1.376 1.32c.815.816 1.901 1.266 3.055 1.266s2.238-.449 3.053-1.266c.817-.814 1.267-1.9 1.267-3.055s-.449-2.238-1.266-3.055a4.296 4.296 0 0 0-3.054-1.264zm-7.576 5.605c-.687.688-1.884.688-2.572 0a1.807 1.807 0 0 1-.533-1.285c0-.486.189-.941.535-1.287.342-.344.799-.533 1.284-.533s.942.189 1.305.551l1.321 1.27-1.34 1.284zm8.861 0c-.687.689-1.866.705-2.59-.018l-1.321-1.27 1.339-1.285c.688-.688 1.886-.688 2.573-.002.344.346.533.801.533 1.287s-.19.944-.534 1.288z"
      fill="currentColor"
    />
  </svg>
));

const Type = styled.div`
  font-weight: 700;
  font-size: ${fontSize(10)};
  line-height: 1;
  text-transform: uppercase;
  color: ${props => props.theme.accent};
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  max-width: 175px;

  ${props =>
    props.type === "real" &&
    css`
      color: ${props => props.theme.alternativeUpper};
    `};

  @media (min-width: 960px) {
    margin-bottom: 4px;
  }
`;

const Balance = styled.div`
  position: relative;
  padding-right: 20px;
  font-weight: 700;
  font-size: 22px;
  line-height: 1;
  color: #fff;
  cursor: pointer;

  ${props =>
    props.showDeposit &&
    css`
      :after {
        content: "";
        display: block;
        position: absolute;
        top: 50%;
        right: 0;
        transform: translateY(-50%)
          rotate(${props => (props.open ? -180 : 0)}deg);
        transform-origin: 50%;
        border: 5px solid transparent;
        border-top-color: ${props => props.theme.accent};
        border-bottom: none;
        transition: transform 0.3s ease;
      }
    `};

  @media (max-width: 959px) {
    padding-top: 2px;
    font-size: 15px;

    :after {
      border-width: 4px;
    }
  }
  @media (max-width: 375px) {
    padding-right: 12px;
  }
`;

const Currency = styled(Money.Symbol)`
  color: ${({ type, theme }) =>
    type === "real" ? theme.alternativeUpper : theme.accent};
`;

const TradingVolume = styled.div`
  direction: ${props => props.theme.direction};
  font-size: ${fontSize(10)};
  line-height: 1;
  color: ${({ theme }) => theme.textMuted};

  @media (min-width: 960px) {
    margin-top: 4px;
  }
`;

const RefillContainer = styled.div`
  display: flex;
  align-items: center;
  font-size: ${fontSize(12)};

  .value {
    color: rgba(255, 255, 255, 0.44);
    margin-left: 7px;
    font-size: 12px;
    &.active {
      color: #ffcc00;
    }
    @media (max-width: 375px) {
      margin-left: ${({ theme }) =>
        theme.locale === "th" || theme.locale === "ar" ? "3px" : "5px"};
    }
  }

  .label {
    @media (max-width: 375px) {
      font-size: ${({ theme }) =>
        (theme.locale === "th" || theme.locale === "ar") && fontSize(10)};
      white-space: ${({ theme }) => theme.locale === "th" && "initial"};
      text-align: ${({ theme }) => theme.locale === "th" && "left"};
      line-height: ${({ theme }) => theme.locale === "th" && ".8"};
    }
  }

  .infinity {
    color: #ffcc00;
    margin: 0 4px;
    width: 12px;
    height: 12px;
    display: flex;
    align-items: center;
  }

  .count {
    font-weight: 400;
    font-size: 12px;
  }
`;

type SharedProps = {
  isOpen: boolean,
  showDeposit: boolean,
  activeAccount: AccountType
};

type AccountProps = {
  ...SharedProps,
  children: Array<Node>
};

export function Account({
  refillAvailable,
  onRefillClick,
  activeAccount,
  isOpen,
  showDeposit,
  children,
  tournamentType,
  totalCountRefill,
  usedCountRefill
}: AccountProps) {
  const { type, balance } = activeAccount;
  let [title, ...content] = children;
  if (!Array.isArray(children)) {
    title = children;
  }

  const RefillInfo = () => {
    if (tournamentType === "free" && !totalCountRefill) {
      return (
        <Fragment>
          <span className="infinity">
            <InfinityIcon />
          </span>
          <span className="count">({usedCountRefill})</span>
        </Fragment>
      );
    } else if (tournamentType === "free" && totalCountRefill) {
      return (
        <span
          className={
            totalCountRefill > usedCountRefill ? "value active" : "value"
          }
        >
          {usedCountRefill}/{totalCountRefill}
        </span>
      );
    }
    return null;
  };

  return (
    <Fragment>
      <Type type={type}>{title}</Type>
      <Balance isOpen={isOpen} showDeposit={showDeposit}>
        {refillAvailable ? (
          <RefillContainer onClick={onRefillClick}>
            <RefreshIcon color="inherit" style={{ fontSize: 16 }} />
            <div className="label">
              <Gettext>Refill</Gettext>
            </div>
            <RefillInfo />
          </RefillContainer>
        ) : (
          <Money
            currency={type === "tournament" ? tournamentCurrency : null}
            Symbol={props => <Currency type={type} {...props} />}
          >
            {balance}
          </Money>
        )}
      </Balance>
      {content}
    </Fragment>
  );
}

type Props = {
  ...SharedProps,
  type?: "real" | "demo" | "tournament",
  profile: Profile
};

function AccountPicker(props: Props) {
  const { type = "demo", ...accountProps } = props;

  switch (type) {
    case "tournament":
      return <TournamentAccount {...accountProps} />;
    case "real":
      const bonus = idx(props, props => props.profile.bonus);

      return (
        <Account {...accountProps}>
          <Gettext>Real account</Gettext>
          {bonus != null && bonus.bonus_sum !== 0 && (
            <TradingVolume>
              <Gettext>Trading volume</Gettext>: <Money>{bonus.turnover}</Money>
            </TradingVolume>
          )}
        </Account>
      );
    case "demo":
    default:
      return (
        <Account {...accountProps}>
          <Gettext>Demo account</Gettext>
        </Account>
      );
  }
}

export default props => (
  <ActiveTournament>
    {tournament => {
      let actualType = "";
      let refillProps = {};

      if (tournament.value != null && props.tradeMode === "tournament") {
        actualType = "tournament";
        refillProps = {
          totalCountRefill: tournament.value.refill_limit,
          usedCountRefill:
            tournament.value.account && tournament.value.account.refill_count,
          tournamentType: tournament.value.type
        };
      } else actualType = props.type;

      return (
        <AccountPicker {...{ ...props, ...refillProps }} type={actualType} />
      );
    }}
  </ActiveTournament>
);



// WEBPACK FOOTER //
// ./src/components/AccountType/Account.js